const LANGUAGE_SUFFIX_PATTERN = /\s*[-–—]\s*(english|japanese|french|german|italian|spanish|portuguese|korean|chinese(?:\s+traditional|\s+simplified)?|thai|indonesian|en|ja|jp)\s*$/i
const LANGUAGE_PAREN_PATTERN = /\s*\((english|japanese|french|german|italian|spanish|portuguese|korean|chinese(?:\s+traditional|\s+simplified)?|thai|indonesian|en|ja|jp)\)\s*$/i

const PRINTING_ALIAS_MAP = new Map<string, string>([
  ['normal', 'Normal'],
  ['standard', 'Normal'],
  ['regular', 'Normal'],
  ['non holo', 'Normal'],
  ['nonholo', 'Normal'],
  ['holo', 'Holofoil'],
  ['holofoil', 'Holofoil'],
  ['foil', 'Holofoil'],
  ['reverse holo', 'Reverse Holofoil'],
  ['reverse holofoil', 'Reverse Holofoil'],
  ['reverse foil', 'Reverse Holofoil'],
  ['holo reverse', 'Reverse Holofoil'],
  ['mirror holo', 'Mirror Holofoil'],
  ['mirror holofoil', 'Mirror Holofoil'],
  ['mirror foil', 'Mirror Holofoil'],
  ['shadowless', 'Shadowless'],
  ['shadowless holo', 'Shadowless Holofoil'],
  ['shadowless holofoil', 'Shadowless Holofoil'],
  ['shadowless foil', 'Shadowless Holofoil'],
  ['1st edition', '1st Edition'],
  ['first edition', '1st Edition'],
  ['1st edition holo', '1st Edition Holofoil'],
  ['1st edition holofoil', '1st Edition Holofoil'],
  ['1st edition foil', '1st Edition Holofoil'],
  ['first edition holo', '1st Edition Holofoil'],
  ['first edition holofoil', '1st Edition Holofoil'],
  ['first edition foil', '1st Edition Holofoil'],
  ['unlimited', 'Unlimited'],
  ['unlimited holo', 'Unlimited Holofoil'],
  ['unlimited holofoil', 'Unlimited Holofoil'],
  ['unlimited foil', 'Unlimited Holofoil'],
  ['manga alternate art', 'Manga Alternate Art'],
  ['manga alt art', 'Manga Alternate Art'],
  ['manga', 'Manga'],
  ['master ball', 'Master Ball'],
  ['masterball', 'Master Ball'],
  ['poke ball', 'Poke Ball'],
  ['pokeball', 'Poke Ball'],
  ['leader parallel', 'Leader Parallel'],
  ['borderless', 'Borderless'],
  ['extended art', 'Extended Art'],
  ['retro frame', 'Retro Frame'],
  ['textured foil', 'Textured Foil'],
  ['textured holo', 'Textured Foil'],
  ['etched foil', 'Etched Foil'],
  ['etched holo', 'Etched Foil'],
  ['showcase', 'Showcase'],
  ['full art', 'Full Art'],
  ['fullart', 'Full Art'],
  ['alternate art', 'Alternate Art'],
  ['alt art', 'Alternate Art'],
  ['illustration rare', 'Illustration Rare'],
  ['special illustration rare', 'Special Illustration Rare'],
  ['hyper rare', 'Hyper Rare'],
  ['secret rare', 'Secret Rare'],
  ['parallel', 'Parallel'],
  ['staff', 'Staff'],
  ['prerelease', 'Prerelease'],
  ['pre release', 'Prerelease'],
  ['stamped', 'Stamped'],
  ['stamp', 'Stamped'],
  ['sealed', 'Sealed'],
])

const normalizePrintingAliasKey = (value: string): string => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()

const STRONG_DESCRIPTOR_RULES: Array<{ label: string; test: (normalized: string) => boolean }> = [
  { label: 'Manga Alternate Art', test: (value) => /\bmanga\b/.test(value) && /\b(alternate\s+art|alt\s+art)\b/.test(value) },
  { label: 'Manga', test: (value) => /\bmanga\b/.test(value) },
  { label: 'Master Ball', test: (value) => /\bmaster\s*ball\b/.test(value) },
  { label: 'Poke Ball', test: (value) => /\bpoke\s*ball\b/.test(value) },
  { label: 'Leader Parallel', test: (value) => /\bleader\b/.test(value) && /\bparallel\b/.test(value) },
  { label: 'Borderless', test: (value) => /\bborderless\b/.test(value) },
  { label: 'Extended Art', test: (value) => /\bextended\s+art\b/.test(value) },
  { label: 'Retro Frame', test: (value) => /\bretro\s+frame\b/.test(value) },
  { label: 'Textured Foil', test: (value) => /\btextured\b/.test(value) },
  { label: 'Etched Foil', test: (value) => /\betched\b/.test(value) },
  { label: 'Showcase', test: (value) => /\bshowcase\b/.test(value) },
  { label: 'Full Art', test: (value) => /\bfull\s*art\b|\bfullart\b/.test(value) },
  { label: 'Alternate Art', test: (value) => /\b(alternate\s+art|alt\s+art)\b/.test(value) },
  { label: 'Illustration Rare', test: (value) => /\billustration\s+rare\b/.test(value) },
  { label: 'Special Illustration Rare', test: (value) => /\bspecial\s+illustration\s+rare\b/.test(value) },
  { label: 'Hyper Rare', test: (value) => /\bhyper\s+rare\b/.test(value) },
  { label: 'Secret Rare', test: (value) => /\bsecret\s+rare\b/.test(value) },
  { label: 'Parallel', test: (value) => /\bparallel\b/.test(value) },
  { label: 'Staff', test: (value) => /\bstaff\b/.test(value) },
  { label: 'Prerelease', test: (value) => /\bprerelease\b/.test(value) },
  { label: 'Stamped', test: (value) => /\bstamped?\b|\bstamp\b/.test(value) },
]

export const normalizePrintingLabel = (printing?: string | null): string => {
  const raw = String(printing || '').trim()
  if (!raw) return 'Normal'

  const cleaned = raw
    .replace(LANGUAGE_SUFFIX_PATTERN, '')
    .replace(LANGUAGE_PAREN_PATTERN, '')
    .replace(/\s+/g, ' ')
    .trim()

  const aliasKey = normalizePrintingAliasKey(cleaned)
  const exactAlias = PRINTING_ALIAS_MAP.get(aliasKey)
  if (exactAlias) return exactAlias

  const normalized = cleaned.toLowerCase()
  const strongDescriptor = STRONG_DESCRIPTOR_RULES.find((rule) => rule.test(normalized))?.label
  if (strongDescriptor && normalizePrintingAliasKey(strongDescriptor) === aliasKey) {
    return strongDescriptor
  }

  return cleaned
}

/** Normalize variant strings for fuzzy substring matching (case/spacing/punctuation). */
export function normalizeVariantToken(value?: string | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}
